/** @format */

import React, { useState } from 'react'
import { _$, Button, Icon, Option, Select, Tooltip } from '@king-design/react'
import styled from 'styled-components'
import { FilterMtchProps } from '../typescript'
import { IconButton } from 'src/components'
import { MatchMode } from './../../SessionAnalysis/typescript'
import variable from 'src/static/styles/var'
import { emptySpanValue } from '../constant'
import { getTraceSpanAttr, getFieldOptions } from 'src/api/ApiAIObservability'
import { useAsync, useAsyncFn, useDebounce } from 'react-use'

const TEXT_MATCH_OPTS: ReadonlyArray<{ value: MatchMode; label: string }> = [
  { value: 'in', label: _$('包含') },
  { value: 'not_in', label: _$('不包含') }
]

interface SpanItemProps {
  Key: string
  Op: MatchMode
  Values: string[]
  index: number
  patchRow: (index: number, patch: Partial<FilterMtchProps>) => void
  removeRow: (index: number) => void
  attrOptions: string[]
  startTime: number
  endTime: number
}

const SpanItem: React.FC<SpanItemProps> = ({
  Key,
  Op,
  Values,
  index,
  patchRow,
  removeRow,
  attrOptions,
  startTime,
  endTime
}) => {
  const [keyword, setKeyword] = useState<string | undefined>(undefined)

  const [{ value: valueOptions, loading }, fetchOptions] = useAsyncFn(
    async (keyword?: string) => {
      if (Key) {
        const result = await getFieldOptions({
          StartTime: startTime,
          EndTime: endTime,
          FieldName: Key,
          Keyword: keyword,
          Limit: 100
        })
        return result || []
      } else {
        return []
      }
    },
    [startTime, endTime, Key]
  )

  useDebounce(
    () => {
      if (keyword === undefined) {
        return
      }
      fetchOptions(keyword)
    },
    500,
    [keyword]
  )
  return (
    <li className="span-attr-filter-row" key={index}>
      <Select
        width={149}
        clearable
        placeholder={_$('属性名')}
        value={Key || undefined}
        filterable
        onChangeValue={v => patchRow(index, { Key: String(v ?? ''), Values: [] })}
      >
        {attrOptions?.map(name => (
          <Option key={name} value={name}>
            {name}
          </Option>
        ))}
      </Select>
      <Select
        width={149}
        clearable
        placeholder={_$('操作符')}
        value={Op ?? 'in'}
        onChangeValue={v => patchRow(index, { Op: v as MatchMode })}
      >
        {TEXT_MATCH_OPTS.map(o => (
          <Option key={o.value} value={o.value}>
            {_$(o.label)}
          </Option>
        ))}
      </Select>
      <Select
        width={149}
        clearable
        multiple
        filterable
        nowrap
        placeholder={_$('属性值')}
        value={Values || undefined}
        onShow={() => fetchOptions()}
        filter={() => true}
        onInput={e => {
          setKeyword((e.target as HTMLInputElement).value.trim())
        }}
        loading={loading}
        onChangeValue={v => patchRow(index, { Values: v as string[] })}
      >
        {valueOptions?.map(o => (
          <Option key={o} value={o}>
            {o}
          </Option>
        ))}
      </Select>
      <IconButton
        className="span-attr-filter-remove"
        type="delete"
        title={_$('删除')}
        onClick={() => removeRow(index)}
      />
    </li>
  )
}

export interface SpanSearchProps {
  className?: string
  value: FilterMtchProps[]
  onChange: (next: FilterMtchProps[]) => void
  startTime: number
  endTime: number
}
const SpanSearch: React.FC<SpanSearchProps> = ({
  className,
  value,
  onChange,
  startTime,
  endTime
}) => {
  const addRow = () => {
    onChange([emptySpanValue(), ...value])
  }
  const patchRow = (index: number, patch: Partial<FilterMtchProps>) => {
    const base = value.map((r, i) => (i === index ? { ...r, ...patch } : r))
    onChange(base)
  }

  const removeRow = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  const { value: attrOptions } = useAsync(async () => {
    if (startTime && endTime) {
      const result = await getTraceSpanAttr({
        StartTime: startTime,
        EndTime: endTime
      })
      return result?.filter(item => item.Category === 'span_attr')?.map(item => item.Name) || []
    }
    return []
  }, [startTime, endTime])

  return (
    <div className={className}>
      <div className="span-attr-filter">
        <div className="span-attr-filter-title">
          <span>{_$('Span 属性筛选')}</span>
          <Tooltip theme="light" content={_$('最多添加5个')} disabled={value?.length < 5}>
            <Button
              type="link"
              disabled={value?.length >= 5}
              onClick={() => {
                addRow()
              }}
            >
              <Icon className="k-icon-add" size="small" />
              {_$('添加')}
            </Button>
          </Tooltip>
        </div>
        <ul className="span-attr-filter-list">
          {value.map((row, index) => (
            <SpanItem
              key={index}
              {...row}
              index={index}
              patchRow={patchRow}
              removeRow={removeRow}
              attrOptions={attrOptions}
              startTime={startTime}
              endTime={endTime}
            />
          ))}
        </ul>
      </div>
    </div>
  )
}

export default styled(SpanSearch)`
  width: 100%;
  margin-top: 16px;
  overflow: hidden;

  .span-attr-filter {
    width: 100%;
    &-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      span {
        font-size: 14px;
      }
      button {
        padding: 0;
      }
    }

    &-list {
      margin: 0;
      padding: 0;
      list-style: none;
    }

    &-row {
      position: relative;
      width: 188px;
      padding: 8px;
      margin-top: 8px;
      background-color: #f8f9fa;
      .k-select {
        margin-bottom: 8px;
        &:last-child {
          margin-top: 0;
        }
      }
    }

    &-remove {
      position: absolute;
      right: 0px;
      top: 8px;
      flex-shrink: 0;
      align-self: center;
      color: var(--k-text-secondary-color, rgba(0, 0, 0, 0.45));
      cursor: pointer;
      transition: color 0.2s;

      &:hover {
        color: ${variable.primaryColor};
      }
    }

    &-add {
      display: block;
      margin-bottom: 8px;
      margin-left: 8px;
      height: 32px;
      line-height: 32px;
      color: var(--k-text-secondary-color, rgba(0, 0, 0, 0.45));
      cursor: pointer;
      transition: color 0.2s;

      &:hover {
        color: ${variable.primaryColor};
      }
    }
  }
`
